import { batch } from "@preact/signals";
import type { ComponentChildren } from "preact";
import {
  cities,
  composers,
  exploreScope,
  historicalCities,
  orgs,
  selectedComposerId,
  selectedPiece,
  setPieceFilter,
} from "../../state/store";
import { useComposerEntity, useFacet, usePieceEntity, type FacetState, type Focus } from "../../state/explore";
import { wikipediaUrl } from "../../data/api";
import { countryDisplayName } from "../../utils/format";

/** Panels fetch the large dims at this depth under a focus so the header's "200+
 *  organizations" and the Organizations panel share one request. */
export const FOCUS_LIMIT = 200;

const PART_KIND_LABEL: Record<string, string> = { overture: "Overture", suite: "Suite", excerpt: "Excerpts" };

interface Stat {
  key: string;
  value: string;
  label: string;
  sub?: string;
  loading: boolean;
}

/** The profile header of a focused entity: what it is, the facts the catalogue holds
 *  about it, and the headline numbers of the current slice. Facts come from
 *  /api/entity (pieces, composers) or the static facet files (organizations, cities,
 *  countries); numbers come from the same facet requests the panels below make. */
export function EntityHeader({ focus, onDrill }: { focus: Focus; onDrill?: () => void }) {
  const piece = usePieceEntity(focus.kind === "piece" ? focus.key : null);
  const composer = useComposerEntity(focus.kind === "composer" ? Number(focus.key) : null);

  const summary = useFacet("country");
  const orgF = useFacet("org", { limit: FOCUS_LIMIT, enabled: focus.kind !== "org" });
  const cityF = useFacet("city", { limit: FOCUS_LIMIT, enabled: focus.kind !== "city" });
  const composerF = useFacet("composer", {
    limit: FOCUS_LIMIT,
    enabled: focus.kind === "org" || focus.kind === "city" || focus.kind === "country",
  });

  const scope = exploreScope.value;
  const stats: Stat[] = [];
  const concertsLoading = summary.loading && summary.matched === 0;
  stats.push({
    key: "concerts",
    value: concertsLoading ? "…" : summary.matched.toLocaleString(),
    label: `${scope === "upcoming" ? "upcoming " : ""}concert${summary.matched === 1 && !concertsLoading ? "" : "s"}`,
    sub: scope !== "upcoming" && summary.matchedUp > 0 ? `${summary.matchedUp.toLocaleString()} upcoming` : undefined,
    loading: concertsLoading,
  });
  const many = (s: FacetState, singular: string, plural: string): Stat => {
    const n = s.buckets.filter((b) => b.key !== "unknown").length;
    const capped = s.truncated || n >= FOCUS_LIMIT;
    const loading = s.loading && s.buckets.length === 0;
    return {
      key: plural,
      value: loading ? "…" : capped ? `${FOCUS_LIMIT}+` : n.toLocaleString(),
      label: n === 1 && !capped && !loading ? singular : plural,
      loading,
    };
  };
  const countriesStat = (): Stat => many(summary, "country", "countries");
  switch (focus.kind) {
    case "piece":
    case "composer":
      stats.push(many(orgF, "organization", "organizations"), countriesStat(), many(cityF, "city", "cities"));
      break;
    case "org":
      stats.push(many(composerF, "composer", "composers"), many(cityF, "city", "cities"));
      break;
    case "city":
      stats.push(many(orgF, "organization", "organizations"), many(composerF, "composer", "composers"));
      break;
    case "country":
      stats.push(many(cityF, "city", "cities"), many(orgF, "organization", "organizations"), many(composerF, "composer", "composers"));
      break;
  }

  let kind = "";
  let name = "";
  const facts: ComponentChildren[] = [];
  let link: { href: string; label: string } | null = null;

  switch (focus.kind) {
    case "piece": {
      const p = piece.data;
      const fallback = selectedPiece.value;
      const partKind = p?.part_kind ?? fallback?.part_kind ?? null;
      kind = (partKind && PART_KIND_LABEL[partKind]) || "Piece";
      name = p?.label ?? fallback?.label ?? (piece.loading ? "…" : "Piece");
      const composerName = p?.composer ?? fallback?.composer ?? null;
      const composerId = p?.composer_id ?? fallback?.composer_id ?? null;
      if (composerName) {
        facts.push(
          composerId !== null ? (
            <button
              type="button"
              class="xp-hero-link"
              title={`Explore ${composerName}`}
              onClick={() => {
                batch(() => {
                  setPieceFilter(null);
                  selectedComposerId.value = composerId;
                });
                onDrill?.();
              }}
            >
              {composerName}
            </button>
          ) : (
            composerName
          ),
        );
      }
      if (p?.catalogue) facts.push(p.catalogue);
      if (p?.genre) facts.push(p.genre);
      if (p?.year) facts.push(`composed ${p.year}`);
      if (p?.parent_label) {
        facts.push(
          p.parent_key ? (
            <>
              from{" "}
              <button
                type="button"
                class="xp-hero-link"
                title={`Explore ${p.parent_label}`}
                onClick={() => {
                  setPieceFilter({
                    key: p.parent_key!,
                    label: p.parent_label!,
                    composer: p.composer,
                    composer_id: p.composer_id,
                    part_kind: null,
                    count: 0,
                  });
                  onDrill?.();
                }}
              >
                {p.parent_label}
              </button>
            </>
          ) : (
            `from ${p.parent_label}`
          ),
        );
      }
      if (p?.wikidata) link = { href: wikipediaUrl(p.wikidata), label: "Wikipedia" };
      break;
    }
    case "composer": {
      const id = Number(focus.key);
      const c = composers.value.find((x) => x.id === id);
      kind = "Composer";
      name = c?.name ?? `Composer #${id}`;
      const birth = composer.data?.birth_year ?? c?.birth_year ?? null;
      const death = composer.data?.death_year ?? c?.death_year ?? null;
      if (birth && death) facts.push(`${birth}–${death}`);
      else if (birth) facts.push(`born ${birth}`);
      else if (death) facts.push(`died ${death}`);
      if (composer.data && composer.data.pieces > 0) {
        facts.push(`${composer.data.pieces.toLocaleString()} piece${composer.data.pieces === 1 ? "" : "s"} in our catalogue`);
      }
      if (composer.data?.wikidata) link = { href: wikipediaUrl(composer.data.wikidata), label: "Wikipedia" };
      break;
    }
    case "org": {
      const id = Number(focus.key);
      const o = orgs.value.find((x) => x.id === id);
      kind = "Organization";
      name = o?.name ?? `Organization #${id}`;
      if (o?.org_type && o.org_type !== "unknown") facts.push(o.org_type.replace(/_/g, " "));
      break;
    }
    case "city": {
      const id = Number(focus.key);
      const c = cities.value.find((x) => x.id === id) ?? historicalCities.value.find((x) => x.id === id);
      kind = "City";
      name = c?.name ?? `City #${id}`;
      if (c) facts.push(countryDisplayName(c.country_code, c.country_name));
      break;
    }
    case "country":
      kind = "Country";
      name = countryDisplayName(focus.key);
      break;
  }

  return (
    <section class={`xp-hero xp-hero-${focus.kind}`} aria-label={`${kind}: ${name}`}>
      <div class="xp-hero-kind">{kind}</div>
      <h2 class="xp-hero-name">{name}</h2>
      {(facts.length > 0 || link) && (
        <p class="xp-hero-facts">
          {facts.map((f, i) => (
            <span key={i} class="xp-hero-fact">
              {f}
            </span>
          ))}
          {link && (
            <a class="xp-hero-fact xp-hero-ext" href={link.href} target="_blank" rel="noopener noreferrer">
              {link.label} ↗
            </a>
          )}
        </p>
      )}
      <div class={`xp-hero-stats${stats.length === 3 ? " n3" : ""}`} role="list">
        {stats.map((s) => (
          <div key={s.key} role="listitem" class={`xp-stat${s.loading ? " is-loading" : ""}`} aria-label={`${s.value} ${s.label}${s.sub ? `, ${s.sub}` : ""}`}>
            <span class="xp-stat-n">{s.value}</span>
            <span class="xp-stat-l">
              {s.label}
              {s.sub && <span class="xp-stat-sub">{s.sub}</span>}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
